import type { BlogPost } from '../types';

export const post: BlogPost = {
  "id": "498",
  "slug": "how-to-spot-a-wildfire-burn-scar-from-satellite-imagery",
  "title": "How to Spot a Wildfire Burn Scar From Satellite Imagery",
  "excerpt": "After a wildfire passes, it leaves a mark visible from orbit for months. Here is how to recognise a burn scar in satellite imagery and read how severe it was.",
  "date": "2026-06-22",
  "category": "Geography",
  "image": "https://images.unsplash.com/photo-1692364221415-654b20e6d1d2?ixid=M3w5NjY4NzN8MHwxfHNlYXJjaHwxfHx3aWxkZmlyZSUyMGZvcmVzdHxlbnwxfDB8fHwxNzgyMTE2MDQyfDA&ixlib=rb-4.1.0&w=800&q=80&fit=crop",
  "readTime": "5 min",
  "author": "EarthGuessr Team",
  "tldr": "A wildfire burn scar shows up in satellite imagery as a dark, irregular patch where vegetation has been charred. In true colour it looks brown or black; in false-colour and burn-ratio imagery it stands out sharply because burned land reflects infrared very differently from healthy plants.",
  "content": [
    {
      "type": "paragraph",
      "text": "A wildfire is fast, but the mark it leaves is slow to fade. Long after the flames are out, the burned ground stays visible from space — a dark, ragged stain across the landscape that can persist for months or even years before plants reclaim it. Learning to spot these burn scars turns a satellite image into a record of where fire has recently swept through."
    },
    {
      "type": "heading",
      "text": "What a Burn Scar Looks Like in True Colour"
    },
    {
      "type": "paragraph",
      "text": "In an ordinary true-colour image, the kind that looks roughly like what your eye would see, a fresh burn scar appears as a patch of dark brown, charcoal, or black where green vegetation used to be. The edges are irregular and often follow the terrain, because fire spreads with the wind and the slope. The most telling feature is the contrast: a sharp boundary between the scorched ground and the healthy land around it."
    },
    {
      "type": "paragraph",
      "text": "Look, too, for unburned islands inside the scar — patches of green that the fire skipped, often where a river, road, or wetter ground stopped its advance. That patchy, mottled texture inside a dark outline is a strong sign you are looking at a burn rather than, say, a ploughed field or bare rock."
    },
    {
      "type": "heading",
      "text": "Why False Colour Makes Burns Pop"
    },
    {
      "type": "paragraph",
      "text": "Satellites see far more than visible light. They also record near-infrared, a band in which healthy vegetation is extremely bright because living leaves reflect it strongly. When fire destroys that vegetation, the near-infrared reflection collapses. So in a false-colour image that maps infrared to a visible colour, thriving plants glow vivid red while a burn scar turns dark and dull — a contrast far stronger than the eye alone could see."
    },
    {
      "type": "paragraph",
      "text": "This is the same principle behind vegetation indices like NDVI, which measure plant health by comparing infrared and red light. A burn scar shows up as a sudden drop in greenness, cleanly outlining the fire’s footprint."
    },
    {
      "type": "heading",
      "text": "Measuring How Badly It Burned"
    },
    {
      "type": "paragraph",
      "text": "Analysts go a step further with the Normalized Burn Ratio, or NBR, which combines near-infrared and shortwave-infrared light. Burned ground and healthy vegetation behave in opposite ways across these two bands, so NBR isolates fire damage especially well. By comparing NBR from before and after a fire — a difference called dNBR — specialists can map not just where it burned but how severely, from lightly singed to completely scorched."
    },
    {
      "type": "list",
      "items": [
        "Dark, irregular patch: the core sign of charred vegetation.",
        "Sharp, ragged boundary: a clean edge between burned and unburned land, shaped by wind and terrain.",
        "Unburned islands: green patches the fire skipped, often near water or roads.",
        "Low infrared reflectance: burns look dark in false-colour and vegetation-index imagery.",
        "Smoke plumes and hotspots: during an active fire, drifting smoke and thermal hotspots reveal the fire front itself."
      ]
    },
    {
      "type": "heading",
      "text": "Active Fire Versus Old Scar"
    },
    {
      "type": "paragraph",
      "text": "It helps to tell a burning fire from a healed one. An active fire often shows a grey-brown smoke plume streaming downwind, and satellites with thermal or shortwave-infrared sensors can pinpoint the glowing fire front even through smoke. A burn scar, by contrast, is what remains afterward: no smoke, no heat, just the dark footprint. Over the following seasons you can sometimes watch a scar slowly green up again as vegetation recovers."
    },
    {
      "type": "heading",
      "text": "Why It Matters"
    },
    {
      "type": "paragraph",
      "text": "Spotting burn scars is more than a curiosity. Satellite imagery lets responders map a fire’s extent quickly, helps scientists track how landscapes recover, and reveals long-term patterns of where fire shapes the land — from the fire-adapted forests of the American West to the grassland burns of Africa and Australia. Free imagery from missions like Sentinel-2 and Landsat has made this kind of monitoring available to anyone willing to look."
    },
    {
      "type": "paragraph",
      "text": "Once you start noticing burn scars, you will see them everywhere on the satellite map, each one a clue about climate, vegetation, and recent events on the ground. Want to sharpen your eye for reading landscapes from above? A few rounds of EarthGuessr are a fun way to practise turning satellite detail into an educated guess about where you are."
    }
  ]
};
